import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Typography from '@src/new-components/Typography';
import WavIcon from '@src/assets/images/wav.svg';
import { COLOR } from '@src/styles/color';
import { roundNumber } from '@src/utils/number';
import { formatDuration } from '@src/services/audio';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import {
  AUDIO_STATUS,
  MAX_AUDIO_DURATION_LIMIT,
  MAX_FILE_SIZE,
  MIN_AUDIO_DURATION_LIMIT,
} from '@src/constants/professionalVoiceCloneUpload';
import { StyledAudioItem } from './index.style';

const SPINNER_SEGMENTS = Array.from({ length: 8 }, (_, order) => ({
  id: `status-segment-${order}`,
  rotation: order * 45,
  delay: order * 0.08,
}));

const UploadingStatusSpinner = () => (
  <div className="status-spinner" role="presentation" aria-hidden="true">
    {SPINNER_SEGMENTS.map((segment) => (
      <span
        key={segment.id}
        className="status-spinner-bar"
        style={{
          transform: `rotate(${segment.rotation}deg) translate(0, -4px)`,
          animationDelay: `${segment.delay}s`,
        }}
      />
    ))}
  </div>
);

const AudioItem = ({ audio, onDelete, onPlay, onAdjust, isPlaying }) => {
  const { t } = useTranslation();

  const audioUploading = audio?.status === AUDIO_STATUS.UPLOADING;
  const audioRating = audio?.status === AUDIO_STATUS.CHECKING_QUALITY;
  const audioDeleting = Boolean(audio?.deleting);

  const hasAudioMaxDuration = audio?.duration > MAX_AUDIO_DURATION_LIMIT;
  const hasAudioMinDuration = audio?.duration < MIN_AUDIO_DURATION_LIMIT;
  const hasAudioFileSize = audio?.size > MAX_FILE_SIZE;

  const isHideAdjustButton =
    hasAudioMaxDuration ||
    hasAudioMinDuration ||
    hasAudioFileSize ||
    audioRating ||
    audioUploading;

  const formattedSize = useMemo(() => {
    if (!audio?.size) return '';
    const sizeInMb = roundNumber(audio.size / (1024 * 1024), 1);
    return `${sizeInMb} MB`;
  }, [audio?.size]);

  const formattedDuration = useMemo(() => {
    const rawDuration = audio?.duration || 0;
    return formatDuration(rawDuration);
  }, [audio?.duration]);

  const improvementValue = audio?.qualityImprovement;

  const uploadProgressValue = audio?.progress;

  const handleDelete = () => {
    if (!onDelete) return;
    onDelete(audio?.id ?? audio);
  };

  const statusContent = useMemo(() => {
    if (!audio?.status) return null;
    if (
      audio?.status === AUDIO_STATUS.UPLOADING ||
      audio?.status === AUDIO_STATUS.CHECKING_QUALITY
    ) {
      return {
        color: COLOR.blueV2,
        icon: <UploadingStatusSpinner />,
        text: t(
          audio?.status === AUDIO_STATUS.UPLOADING
            ? 'uploadingAudioProgress'
            : 'assessingAudioQuality',
        ),
      };
    }
    if (audio?.status === AUDIO_STATUS.ERROR) {
      return {
        color: COLOR.functionalRed.default,
        icon: <ErrorRoundedIcon fontSize="inherit" />,
        text: audio?.errorMessage,
      };
    }
    if (audio?.status === AUDIO_STATUS.SUCCESS) {
      const successText = `${improvementValue > 0 ? '+' : ''}${
        improvementValue > 0 ? (improvementValue || 0).toFixed(1) : 0
      }%`;
      return {
        color: COLOR.green,
        icon: <CheckCircleRoundedIcon fontSize="inherit" />,
        text: successText,
      };
    }
    return null;
  }, [
    audio?.errorMessage,
    audio?.statusMessage,
    improvementValue,
    audio?.status,
    t,
    uploadProgressValue,
  ]);

  const shouldShowStatus = Boolean(statusContent?.text);

  const renderMessageError = () => {
    // Check for duration errors
    if (!hasAudioMinDuration && !hasAudioMaxDuration && !hasAudioFileSize)
      return null;
    let message = '';
    if (hasAudioFileSize) {
      message = t('audioFileSizeTooLarge');
    } else if (hasAudioMinDuration) {
      message = t('audioDurationLeastSeconds', {
        seconds: MIN_AUDIO_DURATION_LIMIT,
      });
    } else if (hasAudioMaxDuration) {
      message = t('audioDurationTooLongMinutes', {
        minutes: MAX_AUDIO_DURATION_LIMIT / 60,
      });
    }
    return (
      <Typography variant="body-14-regular" color={COLOR.functionalRed.default}>
        {message}
      </Typography>
    );
  };

  return (
    <StyledAudioItem status={audio?.status}>
      <div className="audio-item-content">
        <div className="file-type-icon">
          <img src={WavIcon} alt={audio?.name || 'audio file'} />
        </div>
        <div className="audio-item-info">
          <Typography variant="body-16-semibold" className="audio-item-name">
            {audio?.name}
          </Typography>
          <div className="audio-item-meta">
            {formattedDuration && (
              <Typography variant="body-14-regular" color={COLOR.label2[64]}>
                {formattedDuration}
              </Typography>
            )}
            {formattedDuration && formattedSize && (
              <span className="audio-item-dot" />
            )}
            {formattedSize && (
              <Typography variant="body-14-regular" color={COLOR.label2[64]}>
                {formattedSize}
              </Typography>
            )}
            {(formattedDuration || formattedSize) && shouldShowStatus && (
              <span className="audio-item-dot" />
            )}
            {shouldShowStatus && statusContent && (
              <div
                className={`audio-item-meta-status status-${
                  audio?.status || 'default'
                }`}
                style={{ color: statusContent.color }}
              >
                {statusContent.icon}
                <Typography
                  variant="body-14-regular"
                  color={statusContent.color}
                  className="audio-item-status-text"
                >
                  {statusContent.text}
                </Typography>
              </div>
            )}
          </div>
          {renderMessageError()}
        </div>
      </div>
      <div className="audio-item-actions">
        <IconButton
          className="action-button"
          aria-label={
            isPlaying
              ? t('pauseAudio', { defaultValue: 'Pause audio' })
              : t('playAudio', { defaultValue: 'Play audio' })
          }
          onClick={() => onPlay?.(audio)}
        >
          {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
        </IconButton>
        {!isHideAdjustButton && (
          <IconButton
            className="action-button"
            aria-label={t('adjustAudio', { defaultValue: 'Adjust effects' })}
            onClick={() => onAdjust?.(audio)}
          >
            <TuneRoundedIcon />
          </IconButton>
        )}
        <IconButton
          className="action-button"
          aria-label={t('deleteAudio', { defaultValue: 'Delete audio' })}
          onClick={audioDeleting ? undefined : handleDelete}
          disabled={audioDeleting}
        >
          {audioDeleting ? (
            <CircularProgress
              size={20}
              thickness={5}
              sx={{ color: COLOR.indigo[80] }}
            />
          ) : (
            <DeleteOutlineRoundedIcon />
          )}
        </IconButton>
      </div>
    </StyledAudioItem>
  );
};

export default AudioItem;
